import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const PricingEditor = () => {
  const [plans, setPlans] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: 0,
    period: 'month',
    description: '',
    features: [''],
    cta_text: '',
    secondary_cta_text: '',
    image_url: '',
    is_popular: false,
    order: 0
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/pricing`);
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const cleanedFeatures = formData.features.filter(f => f.trim() !== '');
      const data = { ...formData, features: cleanedFeatures };
      
      if (editingPlan) {
        await axios.put(`${BACKEND_URL}/api/admin/pricing/${editingPlan.id}`, data);
        toast({ title: 'Success', description: 'Pricing plan updated successfully' });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/pricing`, data);
        toast({ title: 'Success', description: 'Pricing plan created successfully' });
      }
      setIsOpen(false);
      resetForm();
      fetchPlans();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save pricing plan', variant: 'destructive' });
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData(plan);
    setIsOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pricing plan?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/pricing/${id}`);
      toast({ title: 'Success', description: 'Pricing plan deleted successfully' });
      fetchPlans();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete pricing plan', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      price: 0,
      period: 'month',
      description: '',
      features: [''],
      cta_text: '',
      secondary_cta_text: '',
      image_url: '',
      is_popular: false,
      order: 0
    });
    setEditingPlan(null);
  };

  const addFeature = () => {
    setFormData({
      ...formData,
      features: [...formData.features, '']
    });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  return (
    <Card className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Pricing Plans</h2>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="bg-pink-500 hover:bg-pink-600">
              <Plus className="w-4 h-4 mr-2" />
              Add Plan
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPlan ? 'Edit Pricing Plan' : 'Add Pricing Plan'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Plan Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Basic"
                  />
                </div>
                <div>
                  <Label>Price</Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Period</Label>
                  <Input
                    value={formData.period}
                    onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                    placeholder="e.g., month"
                  />
                </div>
                <div>
                  <Label>Order</Label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div>
                <Label>Features</Label>
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex space-x-2 mb-2">
                    <Input
                      value={feature}
                      onChange={(e) => updateFeature(index, e.target.value)}
                      placeholder="Feature item"
                    />
                    <Button size="sm" variant="destructive" onClick={() => removeFeature(index)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button size="sm" onClick={addFeature} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Feature
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>CTA Text</Label>
                  <Input
                    value={formData.cta_text}
                    onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                    placeholder="e.g., Get Started"
                  />
                </div>
                <div>
                  <Label>Secondary CTA</Label>
                  <Input
                    value={formData.secondary_cta_text}
                    onChange={(e) => setFormData({ ...formData, secondary_cta_text: e.target.value })}
                    placeholder="e.g., Contact Sales"
                  />
                </div>
              </div>
              <div>
                <Label>Image URL</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="Paste from Media Library"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.is_popular}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_popular: checked })}
                />
                <Label>Mark as Popular</Label>
              </div>
              <Button onClick={handleSubmit} className="w-full">
                {editingPlan ? 'Update' : 'Create'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <Card key={plan.id} className={`p-6 ${plan.is_popular ? 'ring-2 ring-pink-500' : ''}`}>
            {plan.is_popular && (
              <div className="text-xs bg-pink-500 text-white px-3 py-1 rounded-full inline-block mb-3">
                Most Popular
              </div>
            )}
            <img src={plan.image_url} alt={plan.name} className="w-full h-32 object-cover rounded-lg mb-4" />
            <h3 className="font-bold text-2xl mb-2">{plan.name}</h3>
            <div className="text-4xl font-bold mb-2">
              ${plan.price}
              <span className="text-lg text-gray-500">/{plan.period}</span>
            </div>
            <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
            <ul className="space-y-2 mb-4">
              {plan.features.map((feature, i) => (
                <li key={i} className="text-sm">• {feature}</li>
              ))}
            </ul>
            <div className="flex space-x-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => handleEdit(plan)} className="flex-1">
                <Edit className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="destructive" onClick={() => handleDelete(plan.id)} className="flex-1">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {plans.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No pricing plans added yet
        </div>
      )}
    </Card>
  );
};

export default PricingEditor;
